import React, { FC, useMemo, useCallback, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import _isEmpty from 'lodash/isEmpty';
import { SelectOption } from '@minecraft.typings';
import { gqlRepresentationTypeEnum as REPRESENTATION_TYPE, gqlListSortOrderEnum } from '@minecraft.graphql-types';
import ErrorMessage from '@minecraft.error-message';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import { FilterData } from '../../../shared/types';
import { getRepresentativeName, generateSelectOptionsBasedOnField } from '../../../shared/utils';
import { BasicFormItem } from '../../../shared/styles';
import {
  QUERY_REPRESENTATIVE_LISTS,
  QueryRepListOutput,
  SEARCH_REPRESENTATIVES,
  SEARCH_REPRESENTATIVES_BY_NAME,
} from '../FilterBasicCollectionGraphQL';

type Props<T = FilterData> = {
  representativeList?: T;
  representatives?: T;
  searchByRoleSubmissionAndName?: boolean;
  roleId?: number | string;
};

const Representation: FC<Props> = ({ representativeList, representatives, searchByRoleSubmissionAndName, roleId }) => {
  const [
    getRepresentativeList,
    { data: representativeListData, error: representativeListError, loading: representativeListLoading },
  ] = useLazyQuery<QueryRepListOutput>(QUERY_REPRESENTATIVE_LISTS, {
    variables: {
      input: {
        sortOrder: gqlListSortOrderEnum.ALPHABETICAL,
      },
    },
  });

  const [
    getRepresentatives,
    { data: representativesData, error: representativesError, loading: representativesLoading, fetchMore },
  ] = useLazyQuery(searchByRoleSubmissionAndName ? SEARCH_REPRESENTATIVES_BY_NAME : SEARCH_REPRESENTATIVES);

  const representativeListOptions = useMemo(() => {
    return generateSelectOptionsBasedOnField(
      representativeListData?.representativeLists?.objects,
      'name'
    ) as SelectOption[];
  }, [representativeListData]);

  const representativesOptions = useMemo(() => {
    const representativesList = searchByRoleSubmissionAndName
      ? representativesData?.organizationsByRoleSubmissionsAndName?.objects?.map((obj) => obj.organization)
      : representativesData?.searchRepresentatives?.organizations;

    if (!representativesList) return [];

    return representativesList.map((representative) => ({
      value: representative.id,
      label: getRepresentativeName({
        divisionName: representative.name,
        companyName: representative?.parentOrganization?.name,
      }),
    }));
  }, [representativesData]);

  const handleRepresentativeListFetch = useCallback((): void => {
    if (!representativeListData) {
      getRepresentativeList();
    }
  }, [representativeListData]);

  const handleRepresentativesFetch = useCallback((searchValue: string, offset, limit): void => {
    getRepresentatives({
      variables: searchByRoleSubmissionAndName
        ? {
            roleId: +roleId,
            divisionOrAgencyName: searchValue || '',
            page: {
              pageNumber: offset,
              pageSize: limit,
            },
          }
        : {
            input: {
              companyName: searchValue,
              representativeTypeCodes: [REPRESENTATION_TYPE.AGENCY, REPRESENTATION_TYPE.MANAGEMENT],
            },
            page: {
              pageNumber: offset,
              pageSize: limit,
            },
          },
    });
  }, []);

  const handleRepresentativesFetchMore = useCallback(
    async (_, offset, limit) => {
      await fetchMore({
        variables: {
          page: {
            pageNumber: offset,
            pageSize: limit,
          },
        },
      });
    },
    [fetchMore]
  );

  const error = representativeListError || representativesError;

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      {!_isEmpty(representativeList) && (
        <BasicFormItem>
          <DropdownWithChildren
            withFormik
            withSearch
            withSelectAll
            isMultipleSelect
            dataTestId="representative-list"
            isLoading={representativeListLoading}
            name={representativeList.name}
            toggleLabel={representativeList.label}
            options={representativeListOptions}
            onOptionsFetch={handleRepresentativeListFetch}
          />
        </BasicFormItem>
      )}
      <BasicFormItem>
        <DropdownWithChildren
          withFormik
          withSearch
          withAutocomplete
          isMultipleSelect
          isLoading={representativesLoading}
          initialOffset={1}
          initialLimit={20}
          name={representatives.name}
          toggleLabel={representatives.label}
          optionsTotalCount={
            searchByRoleSubmissionAndName
              ? representativesData?.organizationsByRoleSubmissionsAndName?.totalCount
              : representativesData?.searchRepresentatives?.totalCount
          }
          options={representativesOptions}
          onOptionsFetch={handleRepresentativesFetch}
          onOptionsFetchMore={handleRepresentativesFetchMore}
        />
      </BasicFormItem>
    </>
  );
};

export default memo(Representation);
